import api from '../api.js'
import dateHelper from '../functions/dateHelper.js'
import errors from '../functions/errors.js'
import eventBus from '../eventBus.js'
import i18n from '../i18n.js'

export function calculateArrivalDates ({ commit }, { currentDate }) {
  const date = dateHelper.dateFromString(currentDate)

  commit('updateArrivalTimeDates', {
    previous: dateHelper.formatDate(dateHelper.dayBefore(date)),
    active: dateHelper.formatDate(date),
    next: dateHelper.formatDate(dateHelper.dayAfter(date))
  })
}

export async function getLabels ({ commit }) {
  const response = await api.get('helper/labels/')
  commit('setLabels', response.data)
}

export async function getConfig ({ commit }) {
  const response = await api.get('helper/config/')
  commit('setConfig', response.data)
}

export async function getAllArticles ({ commit }) {
  const response = await api.get('events/articles/')
  commit('setArticles', response.data)
}

export async function getVoucherTypes ({ commit }) {
  const response = await api.get('vouchers/')
  commit('updateVouchers', response.data)
  return response
}

export async function loadGiftCards ({ commit }) {
  const response = await api.get('giftcards/')
  commit('setGiftcards', this.giftCards)
  return response
}

export function loadGiftCardTemplates () {
  return api.get('giftcards/templates/')
}

export async function getGaps ({ commit }, { date }) {
  const response = await api.get('openings/', {
    params: { date, extend: 1 }
  })

  commit('updateGaps', response.data)
}

export async function getTours ({ commit }, { date, basetype, cls }) {
  const toursRequest = api.get('tours/', {
    params: { date, basetype, class: cls }
  })

  const configRequest = api.get('tours/config/')

  const [tours, config] = await Promise.all([toursRequest, configRequest])

  commit('updateTours', tours.data)
  commit('updateToursConfig', config.data)
}

export async function getToursConfig ({ commit }) {
  const config = await api.get('tours/config/')

  commit('updateToursConfig', config.data)
}

export function updateTicketNumber ({ commit }, payload) {
  commit('updateTicketNumber', parseInt(payload))
}

/**
 * Fetch events for a certain date from API
 */
export async function getEvents ({ commit }, { date }) {
  const response = await api.get('events/', {
    params: { date }
  })

  commit('updateEvents', { response, date })
}

export async function getEventTeaserData ({ commit }, eventNumber) {
  const response = await api.get('events/teaserdata/', {
    params: { event: eventNumber }
  })

  commit('updateEventTeaserData', response.data)
}

export async function getCart ({ dispatch, commit }, { dontResetSelection }) {
  try {
    const response = await api.get('cart/')

    if (response.status === 200) {
      const cart = { ...response.data, dontResetSelection }

      if (cart.total) {
        dispatch('calculateArrivalDates', { currentDate: cart.tickets.date })
      }

      commit('updateCart', cart)

      if (cart.total) {
        commit('setShowTickets', true)
      }
    }
  } catch (error) {
    eventBus.emit('handleError', error.response?.data?.error)
  }
}

export async function addTicketsToCart ({ state, commit }, payload) {
  state.cartLoading = true

  const ticketsSelected = state.ticketSelection.tickets
  const articles = {}

  let price = 0

  for (let i = 0; i < ticketsSelected.length; i++) {
    let article = articles[ticketsSelected[i]]

    if (ticketsSelected[i] in state.articles) {
      price += state.articles[ticketsSelected[i]].price
    }

    if (typeof article === 'undefined') {
      article = { count: 1 }
    } else {
      article.count += 1
    }

    articles[ticketsSelected[i]] = article
  }

  if (price === 0) {
    eventBus.emit('handleError', errors(i18n.locale)[1496816969])
  }

  const cartUpdate = {
    date: payload.gap.date,
    gap: payload.gap.gap,
    articles
  }

  try {
    const response = await api.post('cart/tickets/', cartUpdate)
    commit('updateCart', response.data)
  } catch (error) {
    const errorCode = parseInt(error.response.data.gap?.[0])

    if (Number.isNaN(errorCode)) {
      eventBus.emit('handleError', errors(i18n.locale)[1000])
    } else {
      if (errorCode in errors(i18n.locale)) {
        eventBus.emit('handleError', errors(i18n.locale)[errorCode])
      } else {
        eventBus.emit('handleError', errors(i18n.locale)[1000])
      }
    }
  } finally {
    state.cartLoading = false
  }
}

export async function addToursTicketsToCart (
  { dispatch, commit, state },
  { date, gap, name, time, articles }
) {
  state.cartLoading = true

  const articlesInCart = state.cart.tours.articles
  const mergedArticles = {}

  for (const id in articlesInCart) {
    mergedArticles[id] = []

    articlesInCart[id].forEach(item => {
      mergedArticles[id].push({
        count: item.count,
        gap: item.gap,
        name: item.name,
        time: item.time
      })
    })
  }

  for (const id in articles) {
    const articlesForID = mergedArticles[id]
    const count = articles[id]

    if (!count) {
      continue
    }

    const article = { gap, name, time, count }

    if (articlesForID) {
      const articleFromCart = articlesForID.find(item => item.gap === gap)

      if (articleFromCart) {
        articleFromCart.count += count
      } else {
        articlesForID.push(article)
      }
    } else {
      mergedArticles[id] = [article]
    }
  }

  try {
    const response = await api.post('cart/tours/', { date, articles: mergedArticles })
    commit('updateCart', response.data)

    return response
  } catch (error) {
    const errorCode = error.response.data.code
    eventBus.emit('handleError', errors(i18n.locale)[errorCode])
    dispatch('getTours', { date })
  } finally {
    state.cartLoading = false
  }
}

export async function addEventTicketsToCart ({ state, commit }, payload) {
  state.cartLoading = true

  try {
    const response = await api.post('cart/events/', payload)

    if (response.status === 200) {
      commit('updateCart', { ...response.data, dontResetSelection: true })
    } else {
      eventBus.emit('handleError')
    }
  } catch (error) {
    const errorCode = parseInt(error.response.data.gap[0])

    if (Number.isNaN(errorCode)) {
      eventBus.emit('handleError', errors(i18n.locale)[1000])
    } else {
      if (errorCode in errors(i18n.locale)) {
        eventBus.emit('handleError', errors(i18n.locale)[errorCode])
      } else {
        eventBus.emit('handleError', errors(i18n.locale)[1000])
      }
    }
  } finally {
    state.cartLoading = false
  }
}

export async function addGiftCardsToCart ({ state, commit, dispatch }, payload) {
  state.cartLoading = true

  let url = ''

  if (payload.type === 'tickets') {
    url = 'cart/tickets/'
    dispatch('removeFromCart', { endpoint: 'events' })
  } else {
    url = 'cart/events/'
    dispatch('removeFromCart', { endpoint: 'tickets', dontResetSelection: true })
  }

  try {
    const response = await api.post(url, payload.data)

    if (response.status === 200) {
      commit('updateCart', response.data)
    }
  } catch (error) {
    const errorCode = parseInt(error.response.data.gap[0])

    if (Number.isNaN(errorCode)) {
      eventBus.emit('handleError', errors(i18n.locale)[1000])
    } else {
      if (errorCode in errors(i18n.locale)) {
        eventBus.emit('handleError', errors(i18n.locale)[errorCode])
      } else {
        eventBus.emit('handleError', errors(i18n.locale)[1000])
      }
    }
  } finally {
    state.cartLoading = false
  }
}

export async function addReservationToCart ({ state, commit }, payload) {
  state.cartLoading = true

  commit('updateReservationAmount', payload.amount)

  const cartUpdate = {
    date: payload.gap.date,
    gap: payload.gap.gap,
    count: payload.amount
  }

  try {
    const response = await api.post('cart/reservations/', cartUpdate)

    if (response.status === 200) {
      commit('updateCart', response.data)
    } else {
      eventBus.emit('handleError')
    }
  } catch (error) {
    const errorCode = parseInt(error.response.data.gap[0])

    if (Number.isNaN(errorCode)) {
      eventBus.emit('handleError', errors(i18n.locale)[1000])
    } else {
      if (errorCode in errors(i18n.locale)) {
        eventBus.emit('handleError', errors(i18n.locale)[errorCode])
      } else {
        eventBus.emit('handleError', errors(i18n.locale)[1000])
      }
    }
  } finally {
    state.cartLoading = false
  }
}

export async function addVouchersToCart ({ state, commit }, payload) {
  state.cartLoading = true

  const cartUpdate = { articles: {} }

  for (const voucherId in payload) {
    cartUpdate.articles[voucherId] = { count: parseInt(payload[voucherId]) }
  }

  try {
    const response = await api.post('cart/vouchers/', cartUpdate)

    if (response.status === 200) {
      commit('updateCart', response.data)
    } else {
      eventBus.emit('handleError')
    }
  } catch (error) {
    const errorCode = parseInt(error.response.data.gap[0])

    if (Number.isNaN(errorCode)) {
      eventBus.emit('handleError', errors(i18n.locale)[1000])
    } else {
      if (errorCode in errors(i18n.locale)) {
        eventBus.emit('handleError', errors(i18n.locale)[errorCode])
      } else {
        eventBus.emit('handleError', errors(i18n.locale)[1000])
      }
    }
  } finally {
    state.cartLoading = false
  }
}

export async function resetCartTimer ({ commit }) {
  const response = await api.post('cart/timer/')
  commit('setCartTimer', response.data.valid_until)
}

export async function removeVouchersFromCart ({ state, commit }, voucherId) {
  state.cartLoading = true

  const cartUpdate = { articles: { } }
  cartUpdate.articles[voucherId] = { count: 0 }

  try {
    const response = await api.post('cart/vouchers/', cartUpdate)

    if (response.status === 200) {
      commit('updateCart', response.data)
    } else {
      eventBus.emit('handleError')
    }
  } catch (error) {
    eventBus.emit('handleError', error.response.data.error)
  } finally {
    state.cartLoading = false
  }
}

export async function removeFromCart ({ commit }, { endpoint, dontResetSelection }) {
  try {
    const response = await api.delete(`cart/${endpoint}/`)
    commit('updateCart', { ...response.data, dontResetSelection })
  } catch (error) {
    eventBus.emit('handleError', error.response.data.error)
  }
}

export async function clearCart ({ commit }, { dontResetSelection }) {
  try {
    const response = await api.delete('cart')
    commit('updateCart', { ...response.data, dontResetSelection })
  } catch (error) {
    eventBus.emit('handleError', error.response.data.error)
  }
}
